import {Badge} from "primereact/badge";
import {Menu} from "primereact/menu";
import {TabView} from "primereact/tabview";
import {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {matchPath, Outlet, useLocation} from "react-router-dom";

import {SEGMENT} from "@reva/shared/reva-spec";

import AppreciationToolbar from "../component/appreciation/AppreciationToolbar";
import LoadingIndicator from "../component/LoadingIndicator";
import {MainFrame, MainToolbar} from "../component/MainFrame";
import MissingPermissionIndicator from "../component/MissingPermissionIndicator";
import {getMoreTabPanel, getTabPanelWithPermission} from "../component/TabPanelWithPermission";
import {useRevaTranslation} from "../hooks/languageHooks";
import {useNavigateWithQuery} from "../hooks/navigationHooks";
import {useCheckPermissionRequirement} from "../hooks/permissionHooks";
import {selectPermissions} from "../redux/authSlice";
import {selectNotifyEnergyCertificateDecisionRequired} from "../redux/notificationsSlice";
import {
    selectHasFirstFinalizeData,
    selectHasInitialValuation,
    selectHasSubtabParams,
    selectIsBlueHorizon,
} from "../redux/objectParametersSelectors";
import {selectAllIdpEnergyCertParams, selectSegment} from "../redux/objectParametersSlice";
import {
    selectFirstFinalizeDate,
    selectPersistenceInitialized,
    selectPersistenceLoading,
    selectRevaId,
} from "../redux/persistenceSlice";
import {selectObjectDisplayStatus, setObjectDisplayStatus} from "../redux/uiPreferencesSlice";
import {selectIndicationData} from "../redux/valueIndicationSlice";
import {formatDate} from "../util/dateUtil";
import {useEffectRequiresValidRevaId} from "../util/hooks";
import {PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS} from "../util/permissions";
import ObjectInspectionToolbar from "./object/ObjectInspectionToolbar";
import ObjectOverviewToolbar from "./object/ObjectOverviewToolbar";

/**
 * @typedef {TabHeaderWithPermissionParams} TabHeaderWithHiding
 * @property {(AvmSpecSegment) => boolean} [hideTab] dynamic visibility computation
 * @property {(boolean) => boolean} [hideOutputTab] dynamic visibility computation
 * @property {string} [colorKeyFallback] colorKey to use if `hasSubtabParams[colorKey ?? route]` is false, e.g. to also color the tab for a secondary source
 * @property {boolean} [optional] tab is only shown while its persistence layer holds data, otherwise it hides
 *   behind the "+" tab at the end of the tab list (#19159)
 */

/** @type {TabHeaderWithHiding[]} */
const ALL_TABS = [
    {
        route: "overview",
        headerI18n: "object.category.overview",
        colorKey: "reva",
    },
    {
        route: "register",
        headerI18n: "object.category.register",
        hideTab: (segment) => segment === SEGMENT.WHG_M,
    },
    {
        route: "energy",
        headerI18n: "object.category.energy",
        colorKeyFallback: "idpEnergyCertificate",
        hideTab: (segment) => segment === SEGMENT.GRST_K,
    },
    {
        route: "groundvalue",
        headerI18n: "object.category.groundvalue",
        colorKey: "plot",
        appreciationKeyTab: "plot",
        hideTab: (segment) => segment === SEGMENT.WHG_M,
    },
    {
        route: "inspection",
        headerI18n: "object.category.inspection",
        hideTab: (segment) => segment === SEGMENT.WHG_M,
    },
    {
        route: "initialValuation",
        headerI18n: "object.category.initialValuation",
        hideOutputTab: (hasInitialValuation, _) => !hasInitialValuation,
    },
    {
        route: "finalize",
        colorKey: "firstFinalize",
        headerI18n: "object.category.finalize",
        hideOutputTab: (_, showFirstFinalize) => !showFirstFinalize,
    },
    {
        route: "immodrop",
        headerI18n: "object.category.immodrop",
        colorKey: "immodrop",
        optional: true,
    },
];

/**
 * @returns {React.JSX.Element}
 */
function PageObject() {
    const {t, language} = useRevaTranslation();
    const navigate = useNavigateWithQuery();
    const location = useLocation();
    const {pathname} = location;

    const valueIndicationData = useSelector(selectIndicationData);
    const permissions = useSelector(selectPermissions);
    const isBlueHorizon = useSelector(selectIsBlueHorizon);
    const hasInitialValuation = useSelector(selectHasInitialValuation);
    const firstFinalizeDate = useSelector(selectFirstFinalizeDate);
    const showFirstFinalize = useSelector(selectHasFirstFinalizeData);
    const hasSubtabParams = useSelector(selectHasSubtabParams);
    const revaId = useSelector(selectRevaId);
    const segment = useSelector(selectSegment);
    // Badge am Energieausweis-Tab: Backend meldet >1 Energieausweis bei leerer IDP-Schicht. Zusätzlich live
    // gegen die IDP-Schicht gegatet, damit der Badge sofort verschwindet, sobald ein EAusweis ausgewertet wurde.
    const notifyEnergyCertificateDecisionRequired = useSelector(selectNotifyEnergyCertificateDecisionRequired);
    const idpEnergyCertParams = useSelector(selectAllIdpEnergyCertParams);
    const energyCertificateDecisionPending =
        notifyEnergyCertificateDecisionRequired &&
        (!idpEnergyCertParams || Object.keys(idpEnergyCertParams).length === 0);
    const persistenceLoading = useSelector(selectPersistenceLoading);
    const persistenceInitialized = useSelector(selectPersistenceInitialized);
    const dispatch = useDispatch();
    // Lives in the Redux store so the chosen view survives switching between the main
    // pages within the session (#18983); initialized in effect, depending on isBlueHorizon.
    const displayStatus = useSelector(selectObjectDisplayStatus);
    const setDisplayStatus = (status) => dispatch(setObjectDisplayStatus(status));
    const [appreciationForTabVisible, setAppreciationForTabVisible] = useState(false);
    const moreMenuRef = useRef(null);

    // Ein optionaler Tab ist sichtbar, sobald seine Schicht Werte trägt — oder solange er gerade offen ist.
    // Die Route selbst ist damit der „aufgeklappt"-Zustand: wer den leeren Tab nur ansieht und weiternavigiert,
    // findet ihn danach wieder unter "+" (#19159). Beim Löschen der Schicht bleibt er bis zum Verlassen stehen.
    const isOptionalTabHidden = (tab) =>
        !!tab.optional && !hasSubtabParams[tab.colorKey ?? tab.route] && !matchPath(`/object/${tab.route}`, pathname);

    const applicableTabs = ALL_TABS.filter(
        (tab) => !(tab.hideOutputTab?.(hasInitialValuation, showFirstFinalize) || tab.hideTab?.(segment)),
    );
    const hiddenOptionalTabs = applicableTabs.filter(isOptionalTabHidden);

    const tabs = applicableTabs
        .filter((tab) => !isOptionalTabHidden(tab))
        .map((tab) => {
            const {route, disabledFn, colorKey, colorKeyFallback} = tab;
            let {disabled} = tab;
            if (typeof disabledFn === "function") {
                disabled = disabledFn(valueIndicationData);
            }
            const primaryColorKey = colorKey ?? route;
            const effectiveColorKey = hasSubtabParams[primaryColorKey]
                ? primaryColorKey
                : colorKeyFallback && hasSubtabParams[colorKeyFallback]
                  ? colorKeyFallback
                  : "";

            return {
                ...tab,
                colorKey: effectiveColorKey,
                hasTooltip: true,
                disabled,
                date: formatDate(firstFinalizeDate, language),
                badge:
                    route === "energy" && energyCertificateDecisionPending ? (
                        <span className="ml-2" title={t("object.energy.certificate.decisionBadgeTooltip")}>
                            <Badge severity="warning" />
                        </span>
                    ) : undefined,
            };
        });

    useEffectRequiresValidRevaId();

    // Initialize display status, after Reva-Valuation is loaded and "isBlueHorizon" is known
    useEffect(() => {
        if (revaId && !persistenceLoading && !displayStatus) {
            dispatch(setObjectDisplayStatus(isBlueHorizon ? "required" : "default"));
        }
    }, [dispatch, displayStatus, revaId, persistenceLoading, isBlueHorizon]);

    let activeIndex = 0;
    for (let index = 0; index < tabs.length; index++) {
        if (matchPath(`/object/${tabs[index].route}`, pathname)) {
            activeIndex = index;
            break;
        }
    }

    const onTabChange = (route) => {
        setAppreciationForTabVisible(false);
        navigate(`/object/${route}`);
    };

    const onTabChangeByIndex = (index) => {
        if (index >= 0 && index < tabs.length) {
            onTabChange(tabs[index].route);
        }
    };

    const hasAnyPermissions = useCheckPermissionRequirement(PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS);
    if (!hasAnyPermissions) {
        // While the valuation is still (re)loading its snapshot state is not yet known, so the
        // permission requirement cannot be resolved reliably. Show a loading indicator instead of
        // briefly flashing the missing-permission hint until we can tell whether this is a
        // snapshot (which needs no indication permission). See #18799.
        if (persistenceLoading || !persistenceInitialized) {
            return <LoadingIndicator loading />;
        }
        return <MissingPermissionIndicator missingPermissions={true} />;
    }

    const appreciationKeyTab = tabs[activeIndex].appreciationKeyTab ?? tabs[activeIndex].route;

    return (
        <MainFrame>
            <MainToolbar title={t("navigation.object")}>
                <TabView
                    className="flex-1 HeaderTabView"
                    panelContainerClassName="hidden"
                    activeIndex={activeIndex}
                    onTabChange={(e) => onTabChangeByIndex(e.index)}
                >
                    {tabs.map((tab) => getTabPanelWithPermission(tab, t, permissions))}
                    {hiddenOptionalTabs.length > 0 && getMoreTabPanel(t, (event) => moreMenuRef.current?.toggle(event))}
                </TabView>
                {hiddenOptionalTabs.length > 0 && (
                    <Menu
                        popup
                        // an den Body, sonst schneidet die Toolbar das Popup ab
                        appendTo={document.body}
                        ref={moreMenuRef}
                        model={hiddenOptionalTabs.map((tab) => ({
                            label: t(tab.headerI18n),
                            command: () => onTabChange(tab.route),
                        }))}
                    />
                )}
                {activeIndex === 0 && (
                    <ObjectOverviewToolbar
                        displayStatus={displayStatus ?? "default"}
                        setDisplayStatus={setDisplayStatus}
                    />
                )}
                {tabs[activeIndex].route === "inspection" && (
                    <ObjectInspectionToolbar
                        page="object"
                        tab={appreciationKeyTab}
                        appreciationVisible={appreciationForTabVisible}
                        setAppreciationVisible={setAppreciationForTabVisible}
                    />
                )}
                {activeIndex > 0 && tabs[activeIndex].route !== "inspection" && (
                    <AppreciationToolbar
                        page="object"
                        tab={appreciationKeyTab}
                        appreciationVisible={appreciationForTabVisible}
                        setAppreciationVisible={setAppreciationForTabVisible}
                    />
                )}
            </MainToolbar>
            <Outlet
                context={{
                    displayStatus: displayStatus ?? "default",
                    appreciation: {
                        page: "object",
                        tab: appreciationKeyTab,
                        visible: appreciationForTabVisible,
                    },
                }}
            />
        </MainFrame>
    );
}

export default PageObject;
